import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Badge, Empty } from "@/components/admin/Ui";
import { ChatCircleDots, UserPlus } from "@phosphor-icons/react";

export default function ChatSessions() {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/chatbot/sessions").then(({ data }) => {
      setSessions(data.sessions || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-chat-page">
      <PageHeader title="Website Chatbot" subtitle="EPAS Concierge · GPT-5-mini" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-5 bg-white border border-black/10">
          <div className="px-5 py-3 border-b border-black/10 overline">Sessions ({sessions.length})</div>
          {loading ? <div className="p-6 text-navy/60 text-sm">Loading…</div> :
           sessions.length === 0 ? <div className="p-5"><Empty label="No chatbot sessions yet." /></div> : (
            <ul className="divide-y divide-black/10 max-h-[70vh] overflow-y-auto">
              {sessions.map((s) => {
                const first = s.messages?.find((m) => m.role === "user")?.content || "(no messages)";
                const sig = s.last_signal || {};
                return (
                  <li
                    key={s.id}
                    onClick={() => setSelected(s)}
                    data-testid={`chat-session-${s.id}`}
                    className={`px-5 py-3 cursor-pointer hover:bg-navy/5 ${selected?.id === s.id ? "bg-navy/5 border-l-2 border-ember" : ""}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-navy text-sm truncate">{sig.company_name || "(anonymous)"}</div>
                      {s.lead_id && <Badge tone="green"><UserPlus size={10} /> Lead</Badge>}
                    </div>
                    <div className="text-xs text-navy/60 truncate mt-0.5">{first}</div>
                    <div className="text-[10px] text-navy/40 mt-1">{new Date(s.updated_at).toLocaleString()}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
        <section className="lg:col-span-7">
          {!selected ? <Empty label="Select a session to view the conversation." /> : (
            <div className="bg-white border border-black/10" data-testid="chat-session-detail">
              <div className="px-5 py-3 border-b border-black/10 flex items-center gap-2">
                <ChatCircleDots size={16} weight="duotone" className="text-ember" />
                <span className="font-semibold text-sm text-navy">{selected.last_signal?.company_name || "Anonymous visitor"}</span>
                {selected.lead_id && <Badge tone="green">Lead captured</Badge>}
              </div>
              <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto bg-[#F8F8F8]">
                {(selected.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`text-sm px-3 py-2 max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
              {selected.last_signal && (
                <div className="px-5 py-3 border-t border-black/10 text-xs text-navy/70 bg-navy/5">
                  <span className="overline mr-2">AI signal</span>
                  {JSON.stringify(selected.last_signal)}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
